import { flash, SubmitButton } from "@xola/ui-kit";
import { FormField, requiredString } from "common";
import { useFormikContext } from "formik";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Environment, RemoteGateway, RemoteGatewayName, SellerDefaultGateway } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { getEnvironment } from "../../../helpers/authNetPaymentHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { ConfirmLeaveModal } from "../../Common/ConfirmLeaveModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { InputField } from "../../Field/InputField";
import { ContentLayout } from "../../Layout/ContentLayout";
import { isAuthorizeNetEnabled } from "@/models/Seller";

interface FormValues {
    apiLoginId: string;
    transactionKey: string;
    publicKey: ID;
}

export const AuthorizeNetGateway = () => {
    const [isDisabled, setIsDisabled] = useState(false);
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { defaultGateway } = seller;
    const isExistingAuthNetGateway = isAuthorizeNetEnabled(seller) && !!defaultGateway?.id;

    useEffect(() => {
        setIsDisabled(isExistingAuthNetGateway);
    }, [isExistingAuthNetGateway]);

    const { mutateAsync: saveCredentials, isLoading: isSaving } = useMutation(async (payload: any) => {
        await (isExistingAuthNetGateway
            ? api.put(`/users/${seller.id}/remoteGateways/${defaultGateway?.id}`, payload)
            : api.post(`/users/${seller.id}/remoteGateways`, payload));
        invalidate("sellers", seller.id);
        flash.success("Successfully updated payment gateway");
    });

    if (!defaultGateway) {
        return null;
    }

    const creds = isExistingAuthNetGateway ? defaultGateway?.credentials : undefined;

    const initialValues: FormValues = {
        apiLoginId: creds?.apiLoginId ? `***${creds?.apiLoginId.slice(-4)}` : "",
        transactionKey: creds?.apiLoginId ? "*******" : "",
        publicKey: isExistingAuthNetGateway && defaultGateway.publicKey ? defaultGateway.publicKey : "",
    };

    const handleSubmit = async (values: FormValues) => {
        await saveCredentials(getPayload(values, isExistingAuthNetGateway ? defaultGateway : undefined));
        setIsDisabled(true);
    };

    const handleCancel = () => {
        setIsDisabled(true);
    };

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleSubmit }) => (
                <>
                    <ConfirmLeaveModal isFormTouched={!isEqual(initialValues, values)} />
                    <ContentLayout.Section
                        title="Configure Payment Gateway"
                        description="Please provide the Authorize.net Payment Gateway values."
                    >
                        <FormField name="apiLoginId" label="API Login ID">
                            <InputField disabled={isExistingAuthNetGateway} />
                        </FormField>

                        <FormField name="transactionKey" label="Transaction Key">
                            <InputField disabled={isDisabled} />
                        </FormField>
                        <FormField name="publicKey" label="Public Key">
                            <InputField disabled={isDisabled} />
                        </FormField>
                        <div className="space-x-3">
                            {isDisabled ? (
                                <VerifyCredentialsFooter onEdit={() => setIsDisabled(false)} />
                            ) : (
                                <SaveButtonFooter
                                    isExistingAuthNetGateway={isExistingAuthNetGateway}
                                    isSaving={isSaving}
                                    onCancel={handleCancel}
                                    onSubmit={handleSubmit}
                                />
                            )}
                        </div>
                    </ContentLayout.Section>
                </>
            )}
        </XolaFormik>
    );
};

const getPayload = (values: FormValues, gateway?: SellerDefaultGateway) => {
    return {
        gateway: RemoteGatewayName.AUTHORIZE_NET_CIM,
        object: "remote_gateway",
        default: true,
        createCard: true,
        credentials: {
            apiLoginId: gateway?.credentials?.apiLoginId ?? values.apiLoginId,
            transactionKey: values.transactionKey,
            developerMode: getEnvironment() !== Environment.PRODUCTION,
        },
        publicKey: values.publicKey,
    };
};

const VerifyCredentialsFooter = ({ onEdit }: { onEdit: () => void }) => {
    const seller = useSeller();
    const [isLoading, setIsLoading] = useState(false);
    const { setFieldValue } = useFormikContext<FormValues>();
    const { defaultGateway } = seller;

    const verifyCredentials = async () => {
        try {
            setIsLoading(true);
            const { data: response } = await api.get<RemoteGateway>(
                `/users/${seller.id}/remoteGateways/${defaultGateway?.id}/verify`,
            );
            setIsLoading(false);
            if (response.id) {
                flash.success("Successfully verified");
            }
        } catch {
            setIsLoading(false);
            flash.danger("Verification failed");
        }
    };

    const handleVerify = async () => {
        await verifyCredentials();
    };

    return (
        <div className="flex justify-between">
            <SecondaryButton
                data-testid="authorizeNet-gateway-edit-button"
                disabled={isLoading}
                onClick={() => {
                    setFieldValue("transactionKey", "");
                    onEdit();
                }}
            >
                Edit
            </SecondaryButton>
            <SubmitButton color="secondary" isLoading={isLoading} onClick={handleVerify}>
                Verify Credentials
            </SubmitButton>
        </div>
    );
};

interface SaveButtonFooterProps {
    onCancel: () => void;
    onSubmit: () => void;
    isSaving: boolean;
    isExistingAuthNetGateway: boolean;
}

const SaveButtonFooter = ({ onCancel, onSubmit, isSaving, isExistingAuthNetGateway }: SaveButtonFooterProps) => {
    const { resetForm } = useFormikContext<FormValues>();

    return (
        <>
            <SubmitButton
                type="button"
                isLoading={isSaving}
                data-testid="authorizeNet-gateway-save-button"
                onClick={onSubmit}
            >
                Save
            </SubmitButton>
            {isExistingAuthNetGateway ? (
                <SecondaryButton
                    type="button"
                    onClick={() => {
                        resetForm();
                        onCancel();
                    }}
                >
                    Cancel
                </SecondaryButton>
            ) : null}
        </>
    );
};

const validationSchema = object().shape({
    apiLoginId: requiredString(),
    transactionKey: requiredString(),
    publicKey: requiredString(),
});
